import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { IconEye } from '../../../../../assets/popup-nav-icons';
import { PopUpNavLink } from '../../../../../components';
import { useBackdropContext } from '../../../../../context/backdrop';
import {
  DashboardSections,
  SetHiddenDashboardSectionsMutation,
  SetHiddenDashboardSectionsMutationVariables,
} from '../../../../../data-access/gql-types/graphql';
import { SET_HIDDEN_DASHBOARD_SECTIONS } from '../../../../../data-access/mutations/user';
import { useApplicationStore } from '../../../../../store/application';
import { useUserProfileStore } from '../../../../../store/user-profile';
import { toastError } from '../../../../../utils/toast';

export const useDashboard = () => {
  const { t } = useTranslation('channel-list');
  const { t: tc } = useTranslation('common');
  const { profileData } = useUserProfileStore((s) => s);
  const setBottomNavigationHidden = useApplicationStore((s) => s.setBottomNavigationHidden);
  const [visibleMode, setVisibleMode] = useState<boolean>(false);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const [visibilitySections, setVisibilitySections] = useState<Record<DashboardSections, boolean>>({
    [DashboardSections.None]: false,
    [DashboardSections.Greetings]: false,
    [DashboardSections.TariffSimulation]: false,
    [DashboardSections.WeatherWidget]: false,
    [DashboardSections.QuickActions]: false,
    [DashboardSections.MeterData]: false,
    [DashboardSections.Pv]: false,
    [DashboardSections.Resources]: false,
    [DashboardSections.WhatsNew]: false,
  });
  const [setHiddenDashboardSections] = useMutation<
    SetHiddenDashboardSectionsMutation,
    SetHiddenDashboardSectionsMutationVariables
  >(SET_HIDDEN_DASHBOARD_SECTIONS);

  useEffect(() => {
    if (profileData?.hiddenDashboardSections) {
      const hiddenSections = profileData.hiddenDashboardSections.map(
        (section) => DashboardSections[section as keyof typeof DashboardSections],
      );

      setVisibilitySections((prev) => {
        const newSections = { ...prev };

        Object.keys(newSections).forEach((key) => {
          const sectionKey = key as DashboardSections;
          newSections[sectionKey] = !hiddenSections.includes(sectionKey);
        });

        return newSections;
      });
    }
  }, [profileData]);

  const handleToggleVisibleMode = () => {
    setVisibleMode((prev) => {
      const newMode = !prev;
      setBottomNavigationHidden(newMode);
      return newMode;
    });
  };

  const handleSaveVisibilitySections = () => {
    turnOnBackdrop();

    const dashboardSections = Object.keys(visibilitySections).filter((key) => {
      const sectionKey = key as DashboardSections;
      return !visibilitySections[sectionKey];
    }) as DashboardSections[];

    setHiddenDashboardSections({
      variables: {
        input: {
          dashboardSections,
        },
      },
      onCompleted: (data) => {
        turnOffBackdrop();
        if (!data || !data.setHiddenDashboardSections) {
          toastError({ content: tc('errors.somethingWentWrong') });
          return;
        }

        if (data.setHiddenDashboardSections.result?.ok) {
          handleToggleVisibleMode();
        }
      },
      onError: () => turnOffBackdrop(),
    });
  };

  const navLinks: PopUpNavLink[] = [
    {
      onClick: handleToggleVisibleMode,
      label: !visibleMode ? t('editVisibility') : tc('buttons.cancel'),
      hasLineBelow: true,
      icon: <IconEye />,
    },
  ];

  const toggleVisible = (section: DashboardSections) => {
    setVisibilitySections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return { navLinks, visibleMode, visibilitySections, toggleVisible, handleSaveVisibilitySections };
};
